import React,{useEffect,useState} from 'react';
import {
  createDefaultWeeklyAvailability,
  normalizeWeeklyAvailability,
  SELLER_WEEKDAYS
} from '../../services/sellerAvailabilityService';

const SellerAvailabilityEditor=({
  weeklyAvailability,
  blackoutDates,
  onWeeklyAvailabilityChange,
  onBlackoutDatesChange,
  title='Weekly order availability',
  description='When disabled, your existing unrestricted schedule is preserved.',
  blackoutDescription='Buyers cannot place orders for these dates.'
})=>{
  const [schedule,setSchedule]=useState(
    ()=>normalizeWeeklyAvailability(weeklyAvailability)
  );
  const [enabled,setEnabled]=useState(Boolean(weeklyAvailability));
  const [expanded,setExpanded]=useState(Boolean(weeklyAvailability));
  const [blackoutDraft,setBlackoutDraft]=useState('');

  useEffect(()=>{
    setEnabled(Boolean(weeklyAvailability));
    setSchedule(normalizeWeeklyAvailability(weeklyAvailability));
  },[weeklyAvailability]);

  const updateSchedule=(day,updates)=>{
    const nextSchedule={
      ...schedule,
      [day]:{
        ...schedule[day],
        ...updates
      }
    };

    setSchedule(nextSchedule);
    onWeeklyAvailabilityChange(nextSchedule);
  };

  const toggleSchedule=(checked)=>{
    setEnabled(checked);
    setExpanded(checked);
    const nextSchedule=weeklyAvailability
      ? normalizeWeeklyAvailability(weeklyAvailability)
      : createDefaultWeeklyAvailability();

    setSchedule(nextSchedule);
    onWeeklyAvailabilityChange(checked ? nextSchedule : null);
  };

  const addBlackoutDate=()=>{
    if (!blackoutDraft || blackoutDates.includes(blackoutDraft)) return;

    onBlackoutDatesChange(
      [...blackoutDates,blackoutDraft].sort()
    );
    setBlackoutDraft('');
  };

  return (
    <div className="md:col-span-2 rounded-2xl border border-slate-100 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-xs font-black uppercase tracking-widest text-slate-700">
            {title}
          </h4>
          <p className="mt-1 text-xs font-medium text-slate-400">
            {description}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {enabled && (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={()=>setExpanded((current)=>!current)}
              className="text-xs font-bold text-indigo-600 transition-colors hover:text-indigo-800"
            >
              {expanded ? 'Collapse hours' : 'Expand hours'}
            </button>
          )}
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event)=>toggleSchedule(event.target.checked)}
              className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
            />
            Set weekly hours
          </label>
        </div>
      </div>

      {enabled && expanded && (
        <div className="mt-5 space-y-3">
          {SELLER_WEEKDAYS.map(({key,label})=>(
            <div
              key={key}
              className="grid grid-cols-1 items-center gap-3 rounded-xl bg-slate-50 p-3 sm:grid-cols-[1fr_1fr_1fr]"
            >
              <label className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={schedule[key].enabled}
                  onChange={(event)=>updateSchedule(key,{
                    enabled:event.target.checked
                  })}
                  className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
                {label}
              </label>
              <input
                aria-label={`${label} opening time`}
                type="time"
                step="1800"
                disabled={!schedule[key].enabled}
                value={schedule[key].start}
                onChange={(event)=>updateSchedule(key,{
                  start:event.target.value
                })}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold disabled:opacity-50"
              />
              <input
                aria-label={`${label} closing time`}
                type="time"
                step="1800"
                disabled={!schedule[key].enabled}
                value={schedule[key].end}
                onChange={(event)=>updateSchedule(key,{
                  end:event.target.value
                })}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold disabled:opacity-50"
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-slate-100 pt-5">
        <label className="block text-xs font-black uppercase tracking-widest text-slate-700">
          Blackout dates
        </label>
        <p className="mt-1 text-xs font-medium text-slate-400">
          {blackoutDescription}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            type="date"
            value={blackoutDraft}
            onChange={(event)=>setBlackoutDraft(event.target.value)}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold"
          />
          <button
            type="button"
            onClick={addBlackoutDate}
            disabled={!blackoutDraft || blackoutDates.includes(blackoutDraft)}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-40"
          >
            Add date
          </button>
        </div>
        {blackoutDates.length>0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {blackoutDates.map((date)=>(
              <li
                key={date}
                className="flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700"
              >
                {new Date(`${date}T12:00:00`).toLocaleDateString()}
                <button
                  type="button"
                  aria-label={`Remove blackout date ${date}`}
                  onClick={()=>onBlackoutDatesChange(
                    blackoutDates.filter((item)=>item!==date)
                  )}
                  className="font-black text-indigo-500 hover:text-red-600"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SellerAvailabilityEditor;
